import React, { useEffect, useState } from "react";
import QueueVolumeWidget, { QueueCallVolumeStatus } from "@/components/widgets/call-overview/QueueVolumeWidget";
import QueueCallJourneyTableWidget from "@/components/widgets/call-overview/QueueCallJourneyTableWidget";

export interface QueueWidgetGroupDateParams {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  timeStamp: string;
}

interface QueueWidgetGroupProps {
  queueMicrosoftId: string;
  queueName: string;
  dateParams: QueueWidgetGroupDateParams;
  // Bumped by a with-agents-and-role poll detecting a real change (e.g. an agent renamed) —
  // the Call Journey table's own fetch key is scoped by queue/date only, so an agent rename
  // never touches it; this forces a refetch so stale names don't linger.
  queuesDataVersion?: number;
}

/**
 * TCX-6287 Queue tab: per-Call-Queue widgets. The 3 tab-level overview widgets
 * (Inbound Call Status Distribution, Inbound Handled By Call Queue, Call Volume)
 * and Queue Performance are rendered once by CallOverviewSection instead of per queue.
 */
const WIDGET_KEYS = ["queueVolume", "queueCallJourney"] as const;
type WidgetKey = typeof WIDGET_KEYS[number];

const QueueWidgetGroup: React.FC<QueueWidgetGroupProps> = ({ queueMicrosoftId, queueName, dateParams, queuesDataVersion }) => {
  const { startDate, endDate } = dateParams;

  const [collapsed, setCollapsed] = useState<Record<WidgetKey, boolean>>({
    queueVolume: false,
    queueCallJourney: false,
  });
  const toggleCollapse = (key: WidgetKey) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const bothCollapsed = collapsed.queueVolume && collapsed.queueCallJourney;

  const [drilldown, setDrilldown] = useState<{ status: QueueCallVolumeStatus; callIds: string[] } | null>(null);
  const clearDrilldown = () => setDrilldown(null);

  // Reset the drill-down when the queue or date scope changes — a stale callIds filter
  // should never silently carry over to a different scope.
  useEffect(() => {
    setDrilldown(null);
  }, [queueMicrosoftId, startDate, endDate]);

  return (
    <div>
      <div className={`grid grid-cols-1 lg:grid-cols-4 gap-4 items-stretch ${bothCollapsed ? "" : "lg:h-[264px]"}`}>
        <div className="lg:col-span-1 lg:min-h-0">
          <QueueVolumeWidget
            queueMicrosoftId={queueMicrosoftId}
            queueName={queueName}
            startDate={startDate}
            endDate={endDate}
            collapsed={collapsed.queueVolume}
            onToggleCollapse={() => toggleCollapse("queueVolume")}
            onStatClick={(status, callIds) => setDrilldown({ status, callIds })}
          />
        </div>
        <div className="lg:col-span-3 lg:min-h-0 flex flex-col gap-2">
          <QueueCallJourneyTableWidget
            queueMicrosoftId={queueMicrosoftId}
            queueName={queueName}
            startDate={startDate}
            endDate={endDate}
            collapsed={collapsed.queueCallJourney}
            onToggleCollapse={() => toggleCollapse("queueCallJourney")}
            callIds={drilldown?.callIds}
            drilldownStatus={drilldown?.status}
            onClearDrilldown={clearDrilldown}
            queuesDataVersion={queuesDataVersion}
          />
        </div>
      </div>
    </div>
  );
};

export default QueueWidgetGroup;
